<script setup lang="ts">
import { reactive, ref } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import { useRoute } from 'vue-router';
import router from "../../router";
import { message } from '../../utils/message';
import api from '../../api';
import SettingsSidebar from '../settings/SettingsSidebar.vue';
import AppAlert from '../ui/AppAlert.vue';

const route  = useRoute();
const userId = Number(route.params.id);

interface RuleForm {
  name: string;
  email: string;
  phone: string;
  user_type: string;
}

const formError   = ref("");
const loading     = ref(false);
const ruleFormRef = ref<FormInstance>();
const ruleForm    = reactive<RuleForm>({ name: "", phone: "", email: "", user_type: "" });

const rules = reactive<FormRules<RuleForm>>({
  name:      [{ required: true, message: "Please enter the name of the user",     trigger: "blur" }],
  phone:     [{ required: true, message: "Please enter the user phone number",    trigger: "blur" }],
  email:     [{ required: true, message: "Please enter the user email address",   trigger: "blur" }],
  user_type: [{ required: true, message: "Please select the user type",           trigger: "blur" }],
});

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  await formEl.validate((valid) => {
    if (valid) {
      loading.value = true;
      postData();
    } else {
      formError.value = "Please fill in all required fields.";
    }
  });
};

const postData = async () => {
  try {
    await api.patch(`customer/users/${userId}`, {
      name: ruleForm.name,
      // API expects role as 'admin'/'user' (select value "2" = Admin, "1" = Normal).
      role: ruleForm.user_type === '2' ? 'admin' : 'user',
    });
    loading.value = false;
    message.success("The user record has been saved");
    router.push("/users");
  } catch {
    loading.value = false;
    formError.value = "User update failed, please retry.";
  }
};

// Pre-populate form from user list
api.get('customer/users').then((res: any) => {
  const users = res.data?.data?.users ?? [];
  const user  = users.find((u: any) => Number(u.id ?? u.user_id) === userId);
  if (user) {
    ruleForm.email     = user.email ?? user.user_email ?? '';
    ruleForm.phone     = user.phone ?? user.user_phone ?? '';
    ruleForm.name      = user.name  ?? user.user_name  ?? '';
    // API returns role as 'admin'/'user'; the select uses value "2" (Admin) / "1" (Normal).
    ruleForm.user_type = (user.role ?? user.user_type) === 'admin' ? '2' : '1';
  }
}).catch(() => { formError.value = 'Could not load this user.'; });
</script>

<template>
  <div class="st-page" v-loading="loading">

    <div class="st-header">
      <div class="st-inner">
        <h1 class="st-title">Settings</h1>
        <p class="st-subtitle">Manage your account and business preferences</p>
      </div>
      <div class="st-header-rule"></div>
    </div>

    <div class="st-inner st-body">
      <SettingsSidebar />

      <div class="st-content">

        <!-- Content header -->
        <div class="st-content-head">
          <router-link to="/business" class="st-back">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
            Team Members
          </router-link>
          <div class="st-content-title">Edit User</div>
        </div>

        <!-- Card -->
        <div class="st-card">
          <div class="st-card-band">User Details</div>

          <div class="st-card-body">
            <AppAlert :message="formError" />

            <el-form
              ref="ruleFormRef"
              :model="ruleForm"
              :rules="rules"
              label-width="0px"
              status-icon
            >
              <div class="st-grid">
                <div class="st-field">
                  <label class="st-label">Full Name <span class="st-req">*</span></label>
                  <el-form-item prop="name" class="st-fi">
                    <input v-model="ruleForm.name" class="st-input" placeholder="Enter the name of the user" />
                  </el-form-item>
                </div>

                <div class="st-field">
                  <label class="st-label">Email <span class="st-req">*</span></label>
                  <el-form-item prop="email" class="st-fi">
                    <input v-model="ruleForm.email" class="st-input" type="email" placeholder="Enter the user email" />
                  </el-form-item>
                </div>

                <div class="st-field">
                  <label class="st-label">Phone Number</label>
                  <el-form-item prop="phone" class="st-fi">
                    <input v-model="ruleForm.phone" class="st-input" placeholder="Enter the user phone number" disabled />
                  </el-form-item>
                  <span class="st-hint">Only the users can change their own phone numbers</span>
                </div>

                <div class="st-field">
                  <label class="st-label">Role <span class="st-req">*</span></label>
                  <el-form-item prop="user_type" class="st-fi">
                    <select v-model="ruleForm.user_type" class="st-input st-select">
                      <option value="" disabled>Please select the user type</option>
                      <option value="2">Admin</option>
                      <option value="1">Normal</option>
                    </select>
                  </el-form-item>
                </div>
              </div>

              <div class="st-actions">
                <button class="st-btn" @click.prevent="submitForm(ruleFormRef)" :disabled="loading">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Update
                </button>
              </div>
            </el-form>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
* { box-sizing: border-box; }

.st-page { font-family: 'DM Sans', sans-serif; background: white; min-height: 100%; }
.st-inner { max-width: 1280px; margin: 0 auto; padding: 0 32px; }

.st-header { background: white; padding: 24px 0 0; }
.st-header .st-inner { padding-bottom: 18px; }
.st-title { font-size: 1.4rem; font-weight: 700; color: #0f1729; margin: 0 0 3px; }
.st-subtitle { font-size: 0.83rem; color: #94a3b8; margin: 0; }
.st-header-rule { height: 1.5px; background: #e8f0ff; }

.st-body { display: flex; gap: 24px; padding-top: 28px; padding-bottom: 40px; align-items: flex-start; }
.st-content { flex: 1; min-width: 0; }

/* Content header */
.st-content-head { margin-bottom: 16px; }
.st-back {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 0.8rem; font-weight: 600; color: #0061eb;
  text-decoration: none; margin-bottom: 8px;
  transition: gap 0.15s;
}
.st-back:hover { gap: 9px; }
.st-content-title { font-size: 1.15rem; font-weight: 700; color: #0f1729; }

/* Card */
.st-card { background: white; border: 1.5px solid #ccdeff; border-radius: 14px; overflow: hidden; }
.st-card-band {
  background: #f0f6ff; border-bottom: 1.5px solid #ccdeff; padding: 13px 28px;
  font-size: 0.67rem; font-weight: 700; color: #6b86b8;
  text-transform: uppercase; letter-spacing: 0.08em;
}
.st-card-body { padding: 28px; }

.st-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px 24px; }
.st-field { display: flex; flex-direction: column; }
.st-label { font-size: 0.78rem; font-weight: 600; color: #475569; margin-bottom: 6px; }
.st-req { color: #dc2626; }
.st-fi { margin-bottom: 0 !important; }
:deep(.st-fi .el-form-item__content) { display: block; width: 100%; }
:deep(.st-fi .el-form-item__error) { padding-top: 4px; font-size: 0.74rem; }

.st-input {
  width: 100%; height: 42px; padding: 0 14px;
  border: 1.5px solid #e2e8f0; border-radius: 9px;
  font-family: 'DM Sans', sans-serif; font-size: 0.87rem; color: #1e293b;
  outline: none; transition: border-color 0.15s, box-shadow 0.15s; background: white;
}
.st-input:focus { border-color: #0061eb; box-shadow: 0 0 0 3px rgba(0,97,235,0.08); }
.st-input::placeholder { color: #94a3b8; }
.st-input:disabled { background: #f8fafc; color: #94a3b8; cursor: not-allowed; }
.st-select {
  appearance: none; -webkit-appearance: none; cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 13px center;
  padding-right: 38px;
}

.st-hint { font-size: 0.74rem; color: #94a3b8; margin-top: 5px; }

.st-actions {
  display: flex; align-items: center; justify-content: flex-end; gap: 12px;
  margin-top: 24px; padding-top: 18px; border-top: 1px solid #f1f5f9;
}
.st-btn {
  height: 42px; padding: 0 28px;
  background: #0061eb; color: white; border: none; border-radius: 9px;
  font-family: 'DM Sans', sans-serif; font-size: 0.88rem; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 8px; transition: background 0.15s;
}
.st-btn:hover:not(:disabled) { background: #0050c7; }
.st-btn:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
